import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import ContactForm from '../components/ContactForm';
import Footer from '../components/Footer';

const ContactPage = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi, I'd like to enquire about Agniya Spices");
    window.open(`https://wa.me/918086075558?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Contact Agniya Spices | Wholesale, Bulk & Retail Enquiries</title>
        <meta
          name="description"
          content="Get in touch with Agniya Spices for retail, wholesale, and bulk spice enquiries. WhatsApp, phone, or the contact form — fast response from our Kodungallur, Kerala team."
        />
        <meta
          name="keywords"
          content="contact Agniya Spices, spices wholesale Kerala, bulk spices enquiry, Indian spices contact, Kodungallur spices, Agniya Spices phone, Agniya Spices email"
        />
        <link rel="canonical" href="https://agniyaspices.com/contact" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Agniya Spices | Wholesale, Bulk & Retail Enquiries" />
        <meta
          property="og:description"
          content="Reach us via WhatsApp, phone, or the contact form for retail, wholesale and bulk spice orders across Kerala and India."
        />
        <meta property="og:url" content="https://agniyaspices.com/contact" />
        <meta
          property="og:image"
          content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp"
        />
        <meta property="og:site_name" content="Agniya Spices" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Agniya Spices" />
        <meta
          name="twitter:description"
          content="Reach us via WhatsApp, phone, or the contact form for retail, wholesale and bulk spice orders."
        />
        <meta
          name="twitter:image"
          content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp"
        />

        {/* ContactPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Agniya Spices",
            "url": "https://agniyaspices.com/contact",
            "description": "Contact page for Agniya Spices - retail, wholesale, and bulk enquiries.",
            "mainEntity": {
              "@type": "Organization",
              "name": "Agniya Spices",
              "url": "https://agniyaspices.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Kodungallur",
                "addressRegion": "Kerala",
                "addressCountry": "IN"
              },
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+91-8086075558",
                  "contactType": "Customer Service",
                  "areaServed": "IN",
                  "availableLanguage": ["English", "Malayalam", "Hindi"]
                },
                {
                  "@type": "ContactPoint",
                  "telephone": "+91-8086075558",
                  "contactType": "Sales",
                  "areaServed": "IN",
                  "availableLanguage": ["English", "Malayalam", "Hindi"]
                }
              ]
            }
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://agniyaspices.com" },
              { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://agniyaspices.com/contact" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="contact-header-logo">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">AGNIYA SPICES</div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>

            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">FAQ</Link>
            <Link to="/contact" className="text-[#8B2820] font-semibold transition-colors" data-testid="nav-contact-page">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center mb-6">
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/20" data-testid="contact-back-home">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Contact Agniya Spices
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed">
              Retail, wholesale or bulk — we'd love to hear from you. Pick the channel that suits you best.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Channels */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="https://wa.me/918086075558"
              target="_blank"
              rel="noopener noreferrer"
              className="group border border-[#8B2820]/20 rounded-xl p-8 bg-[#f7f5f2] hover:bg-white hover:shadow-lg transition-all"
              data-testid="contact-channel-whatsapp"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2820]/10 flex items-center justify-center mb-4 group-hover:bg-[#8B2820]/20 transition-colors">
                <MessageCircle className="h-6 w-6 text-[#8B2820]" />
              </div>
              <h3 className="text-xl font-semibold text-[#4a3a33] mb-2">WhatsApp</h3>
              <p className="text-[#736c64] mb-3">Fastest response. Chat with our team directly.</p>
              <p className="text-[#8B2820] font-semibold">+91 80860 75558</p>
            </a>

            <a
              href="tel:+918086075558"
              className="group border border-[#8B2820]/20 rounded-xl p-8 bg-[#f7f5f2] hover:bg-white hover:shadow-lg transition-all"
              data-testid="contact-channel-phone"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2820]/10 flex items-center justify-center mb-4 group-hover:bg-[#8B2820]/20 transition-colors">
                <Phone className="h-6 w-6 text-[#8B2820]" />
              </div>
              <h3 className="text-xl font-semibold text-[#4a3a33] mb-2">Phone</h3>
              <p className="text-[#736c64] mb-3">Call us for orders, pricing, or questions.</p>
              <p className="text-[#8B2820] font-semibold">+91 80860 75558</p>
            </a>

            <div
              className="border border-[#8B2820]/20 rounded-xl p-8 bg-[#f7f5f2]"
              data-testid="contact-channel-address"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2820]/10 flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-[#8B2820]" />
              </div>
              <h3 className="text-xl font-semibold text-[#4a3a33] mb-2">Visit / Ship From</h3>
              <p className="text-[#736c64] mb-3">Our base of operations in Kerala, India.</p>
              <p className="text-[#8B2820] font-semibold">Kodungallur, Kerala</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactForm />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Prefer a quick chat?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Drop us a message on WhatsApp — we usually reply within minutes during business hours.
          </p>
          <Button
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-white text-[#8B2820] hover:bg-white/90 px-12 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
            data-testid="contact-cta-whatsapp"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Message us on WhatsApp
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
